import React, { useState } from 'react';
import { CycleEntry, MOOD_OPTIONS } from '../../types';

interface CycleHistoryProps {
  cycles: CycleEntry[];
  onEditCycle?: (cycle: CycleEntry) => void;
}

const CycleHistory: React.FC<CycleHistoryProps> = ({ cycles, onEditCycle }) => {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  const formatDate = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const getDurationDays = (start: string, end?: string): number | null => {
    if (!end) return null;
    const startDate = new Date(start);
    const endDate = new Date(end);
    // Add 1 to include both start and end days
    return Math.ceil((endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)) + 1;
  };

  const getFlowBadge = (intensity: string) => {
    const colorMap: Record<string, string> = {
      light: 'bg-pink-100 text-pink-600',
      medium: 'bg-red-100 text-red-600',
      heavy: 'bg-red-200 text-red-800',
    };
    return (
      <span
        className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
          colorMap[intensity] || 'bg-gray-100 text-gray-600'
        }`}
      >
        {intensity.charAt(0).toUpperCase() + intensity.slice(1)}
      </span>
    );
  };

  const getMoodDisplay = (mood: string) => {
    const option = MOOD_OPTIONS.find((m) => m.value === mood);
    if (!option) return null;
    return { emoji: option.emoji, label: option.label };
  };

  if (cycles.length === 0) {
    return (
      <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-8">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Cycle History</h2>
        <div className="text-center py-8">
          <div className="text-4xl mb-3">🌸</div>
          <p className="text-gray-600">No cycles logged yet.</p>
          <p className="text-sm text-gray-500 mt-1">
            Start tracking your cycles to see your history here.
          </p>
        </div>
      </div>
    );
  }

  const recentCycles = [...cycles]
    .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime())
    .slice(0, 2);

  return (
    <div className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg border border-white/20 p-8">
      <h2 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-pink-500 to-rose-500 flex items-center justify-center shadow-sm">
          <span className="text-white text-lg">📅</span>
        </div>
        Cycle History
      </h2>

      <div className="space-y-3">
        {recentCycles.map((cycle, index) => {
          const isExpanded = expandedIndex === index;
          const duration = getDurationDays(cycle.startDate, cycle.endDate);
          const moodDisplay = cycle.mood ? getMoodDisplay(cycle.mood) : null;

          return (
            <div
              key={cycle.SK}
              className="border border-gray-200 rounded-xl overflow-hidden hover:border-pink-300 hover:shadow-md hover:scale-[1.01] transition-all duration-300"
            >
              <button
                onClick={() => {
                  setExpandedIndex(isExpanded ? null : index);
                  onEditCycle?.(cycle);
                }}
                className="w-full px-4 py-3 flex items-center justify-between bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <span className="text-xl">🩸</span>
                  <div className="text-left">
                    <div className="font-medium text-gray-800 flex items-center gap-2">
                      {formatDate(cycle.startDate)}
                      {cycle.isIrregular && (
                        <span className="text-yellow-600" title="Irregular cycle detected">
                          ⚠️
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-gray-500">
                      {duration !== null ? `${duration} days` : 'Ongoing'}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {getFlowBadge(cycle.flowIntensity)}
                  <svg
                    className={`w-5 h-5 text-gray-400 transition-transform duration-300 ${
                      isExpanded ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </button>

              <div
                className={`transition-all duration-300 ease-in-out overflow-hidden ${
                  isExpanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="px-4 py-3 border-t border-gray-200 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Start Date</span>
                    <span className="text-gray-800 font-medium">{formatDate(cycle.startDate)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">End Date</span>
                    <span className="text-gray-800 font-medium">
                      {cycle.endDate ? formatDate(cycle.endDate) : 'Ongoing'}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm items-center">
                    <span className="text-gray-500">Flow Intensity</span>
                    {getFlowBadge(cycle.flowIntensity)}
                  </div>
                  {cycle.cycleLength && (
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Cycle Length</span>
                      <span className="text-gray-800 font-medium">{cycle.cycleLength} days</span>
                    </div>
                  )}
                  {moodDisplay && (
                    <div className="flex justify-between text-sm items-center">
                      <span className="text-gray-500">Mood</span>
                      <span className="text-gray-800 font-medium">
                        {moodDisplay.emoji} {moodDisplay.label}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CycleHistory;
